import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useAuth, hasRole } from "../../context/AuthContext";
import { db, doc, onSnapshot, subscribeUserNotifications } from "../../lib/firebase";
import { canAccessSales as canAccessSalesFn } from "../../lib/permissions";

const Wrap = styled.div`
  position: relative;
  display: inline-block;
`;

const AvatarButton = styled.button`
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--nd-border-visible);
  background: ${({ photoUrl }) =>
    photoUrl ? `url(${photoUrl}) center/cover no-repeat` : "var(--nd-surface)"};
  cursor: pointer;
  padding: 0;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--nd-text-secondary);
  transition: border-color 200ms cubic-bezier(0.25, 0.1, 0.25, 1);

  &:hover {
    border-color: var(--nd-text-display);
    color: var(--nd-text-display);
  }
`;

const UnreadDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--nd-accent, #d71921);
  color: #fff;
  font-family: 'Space Mono', monospace;
  font-size: 9px;
  line-height: 16px;
  text-align: center;
  border: 1px solid var(--nd-black);
`;

const PersonGlyph = styled.svg`
  width: 16px;
  height: 16px;
  fill: currentColor;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: var(--nd-surface-raised);
  border: 1px solid var(--nd-border-visible);
  border-radius: 10px;
  padding: 12px;
  z-index: 1000;
  display: ${({ open }) => (open ? "block" : "none")};
`;

const Email = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.04em;
  color: var(--nd-text-display);
  word-break: break-all;
  margin-bottom: 4px;
`;

const RoleLabel = styled.div`
  font-family: 'Space Mono', monospace;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--nd-text-disabled);
  margin-bottom: 12px;
`;

const Divider = styled.div`
  height: 1px;
  background: var(--nd-border);
  margin: 8px 0;
`;

const MenuLink = styled.a`
  display: block;
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--nd-text-secondary) !important;
  text-decoration: none !important;
  padding: 8px 4px;
  cursor: pointer;
  background: transparent;
  border: 0;
  width: 100%;
  text-align: left;
  &:hover {
    color: var(--nd-text-display) !important;
  }
`;

const ProfileAvatar = () => {
  const { user, role, tags, signIn, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const [avatarUrl, setAvatarUrl] = useState(null);
  useEffect(() => {
    if (!db || !user || !user.uid) { setAvatarUrl(null); return undefined; }
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        setAvatarUrl((data && data.avatarUrl) || null);
      },
      () => setAvatarUrl(null),
    );
    return () => unsub();
  }, [user]);

  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!user || !user.uid) return undefined;
    const unsub = subscribeUserNotifications(user.uid, (items) => {
      setUnread(items.filter((n) => !n.read && !n.archived).length);
    });
    return () => unsub();
  }, [user]);

  const handleClick = async () => {
    if (!user) {
      await signIn();
      return;
    }
    setOpen((prev) => !prev);
  };

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
  };

  const idpPhotoUrl =
    user && user.providerData && user.providerData.length > 0
      ? (user.providerData.find((p) => p && p.photoURL) || {}).photoURL || null
      : null;
  const photoUrl =
    avatarUrl ||
    (user && user.photoURL ? user.photoURL : null) ||
    idpPhotoUrl;

  return (
    <Wrap ref={wrapRef}>
      <AvatarButton
        type="button"
        aria-label={user ? "Open profile menu" : "Sign in with Google"}
        onClick={handleClick}
        photoUrl={photoUrl}
      >
        {!photoUrl && (
          <PersonGlyph viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.314 0-8 1.657-8 5v1h16v-1c0-3.343-4.686-5-8-5Z" />
          </PersonGlyph>
        )}
        {unread > 0 && <UnreadDot aria-label={`${unread} unread notifications`}>{unread > 9 ? "9+" : unread}</UnreadDot>}
      </AvatarButton>
      {user && (() => {
        const canAccessSales = canAccessSalesFn(role, tags);
        return (
          <Dropdown open={open}>
            <Email>{user.email}</Email>
            <RoleLabel>Role · {role}</RoleLabel>
            <Divider />
            {hasRole(role, "admin") && <MenuLink as="a" href="/admin">Admin</MenuLink>}
            {hasRole(role, "admin") && <MenuLink as="a" href="/admin-audit">Audit log</MenuLink>}
            {canAccessSales && <MenuLink as="a" href="/sales-leads">Leads</MenuLink>}
            {canAccessSales && <MenuLink as="a" href="/sales">Outreach</MenuLink>}
            <MenuLink as="a" href="/notifications">Notifications{unread > 0 ? ` · ${unread > 9 ? "9+" : unread}` : ""}</MenuLink>
            <MenuLink as="a" href="/tools">Tools</MenuLink>
            <MenuLink as="button" type="button" onClick={handleSignOut}>Sign out</MenuLink>
          </Dropdown>
        );
      })()}
    </Wrap>
  );
};

export default ProfileAvatar;
